import { Link } from "@tanstack/react-router";
import { ArrowRight, Compass, Plus } from "lucide-react";
import { Button, Card, EmptyState } from "@samklang/ui";
import { useKartlaggningStore, type Kartlaggning } from "@/stores/kartlaggning-store";
import { formatDate, perspectiveLabel, perspectiveShortLabel } from "./lib";

export function KartlaggningListScreen() {
  const list = useKartlaggningStore((store) => store.listKartlaggningar);
  const kartlaggningar = list();
  return (
    <section className="mx-auto w-full max-w-5xl px-5 py-8 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">Flera perspektiv</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Kartläggningar</h1>
          <p className="mt-3 max-w-2xl text-lg text-foreground-muted">
            Samla elevens, mentorns, vårdnadshavarens och andra professionellas perspektiv och jämför dem i en samlad bild.
          </p>
        </div>
        <Link to="/kartlaggningar/new" className="focus-visible:outline-none">
          <Button>
            <Plus size={20} /> Starta kartläggning
          </Button>
        </Link>
      </div>

      {kartlaggningar.length === 0 ? (
        <div className="mt-10">
          <Card>
            <EmptyState
              icon={<Compass />}
              title="Inga kartläggningar ännu"
              description="Starta en kartläggning för att samla flera perspektiv på en elevs skolsituation."
              action={
                <Link to="/kartlaggningar/new" className="w-full">
                  <Button fullWidth>
                    <Plus size={20} /> Starta kartläggning
                  </Button>
                </Link>
              }
            />
          </Card>
        </div>
      ) : (
        <div className="mt-8 grid items-start gap-4 lg:grid-cols-2">
          {kartlaggningar.map((kartlaggning) => (
            <KartlaggningCard key={kartlaggning.id} kartlaggning={kartlaggning} />
          ))}
        </div>
      )}
    </section>
  );
}

function KartlaggningCard({ kartlaggning }: { readonly kartlaggning: Kartlaggning }) {
  const done = kartlaggning.runs.filter((run) => run.status === "done");
  const remaining = kartlaggning.runs.filter((run) => run.status !== "done");
  const inProgress = remaining.filter((run) => run.status === "in_progress");
  const responsible = [...new Set(remaining.map((run) => run.conductedBy).filter(Boolean))].join(", ");
  return (
    <Link
      to="/kartlaggningar/$kartlaggningId"
      params={{ kartlaggningId: kartlaggning.id }}
      className="rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card padding="lg" className="flex h-full flex-col border-border transition-transform hover:-translate-y-0.5 hover:border-primary/40">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-foreground-muted">{formatDate(kartlaggning.createdAt)}</p>
            <h2 className="mt-1 font-display text-2xl font-semibold">{kartlaggning.title}</h2>
            <p className="mt-1 text-sm">Lokalt id: <span className="font-semibold">{kartlaggning.participantReference || "—"}</span></p>
          </div>
          <ArrowRight aria-hidden="true" className="text-primary" />
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {kartlaggning.runs.map((run) => (
            <span
              key={run.perspective}
              className={
                run.status === "done"
                  ? "inline-flex min-h-9 items-center rounded-full bg-primary-soft px-3 text-sm font-semibold text-primary"
                  : run.status === "in_progress"
                    ? "inline-flex min-h-9 items-center rounded-full bg-warning/15 px-3 text-sm font-semibold text-foreground"
                    : "inline-flex min-h-9 items-center rounded-full bg-surface-muted px-3 text-sm font-medium text-foreground-muted"
              }
            >
              {run.status === "done"
                ? run.conductedWith
                  ? `Klar · ${perspectiveShortLabel(run.perspective, run.role)} med ${run.conductedWith}`
                  : `Klar · ${perspectiveShortLabel(run.perspective, run.role)}`
                : run.status === "in_progress"
                  ? `Pågår · ${perspectiveShortLabel(run.perspective, run.role)}`
                  : `Väntar · ${perspectiveShortLabel(run.perspective, run.role)}`}
            </span>
          ))}
        </div>

        <div className="mt-5 rounded-2xl bg-surface-muted p-4 text-sm leading-relaxed text-foreground-muted">
          <span className="font-semibold text-foreground">
            {done.length} av {kartlaggning.runs.length} klara.
          </span>{" "}
          {remaining.length === 0 ? (
            <span>Alla perspektiv är genomförda. Öppna underlaget för sammanställning och uppföljning.</span>
          ) : (
            <span>
              Återstår: {remaining.map((run) => perspectiveLabel(run.perspective, run.role)).join(", ")}.
              {inProgress.length > 0 ? " En pågår." : ""}
              {responsible ? ` Ansvarar: ${responsible}.` : ""}
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
}
